import React from 'react';
import PropTypes from 'prop-types';
import { PropTypes as MobxPropTypes, observer } from 'mobx-react';
import { DateTime } from 'luxon';

import { constants } from '../../config';
import { SimpleButton } from '../../common/simple-button';
import upArrowIcon from '../../assets/images/icon-drop-up.svg';
import downArrowIcon from '../../assets/images/icon-drop-down.svg';
import { ScreenStore } from '../../stores/ScreenStore';
import { CheckoutSummary } from '../../common/checkout-summary';
import { CheckoutInvoice } from '../../common/checkout-invoice';
import { CHECKOUT_STATUS, CHECKOUT_TYPES } from '../../models/Checkout';
import { OPTION_TYPES } from '../../helpers/ShippingOptionHelper';
import { GlossaryEntry } from '../../common/glossary-entry';
import { GlossaryText } from '../../models/GlossaryText';
import { calculateCartItemAmount } from '../../helpers/CartHelper';
import styles from './CheckoutCard.css';
import { CheckoutMessage } from '../checkout-message';

const BUTTON_TYPES = {
  CANCEL: 'cancel',
  PRINT: 'print',
  PAY: 'pay',
  SHOW_BILLING: 'show_billing',
};

const IPropTypes = {
  isPageMyShoppins: PropTypes.bool,
  buttonTypes: PropTypes.arrayOf(PropTypes.oneOf(Object.values(BUTTON_TYPES))),
  className: PropTypes.string,
  checkout: PropTypes.shape({
    tokenBilling: PropTypes.string,
    address: MobxPropTypes.objectOrObservableObject,
    bank: MobxPropTypes.objectOrObservableObject,
    allowsCoordinations: PropTypes.func.isRequired,
    billingData: MobxPropTypes.objectOrObservableObject,
    cart: MobxPropTypes.arrayOrObservableArray.isRequired,
    checkoutType: PropTypes.oneOf(Object.values(CHECKOUT_TYPES)).isRequired,
    date: PropTypes.instanceOf(DateTime).isRequired,
    information: PropTypes.arrayOf(PropTypes.instanceOf(GlossaryText)),
    informationBottom: PropTypes.arrayOf(PropTypes.instanceOf(GlossaryText)),
    installationData: PropTypes.array,
    paymentType: PropTypes.number,
    shippingData: PropTypes.shape({
      agency: PropTypes.shape({
        extraCost: PropTypes.number.isRequired,
      }),
      date: PropTypes.instanceOf(DateTime),
      endHour: PropTypes.number,
      price: PropTypes.number,
      shippingType: PropTypes.oneOf(Object.values(OPTION_TYPES)),
      startHour: PropTypes.number,
    }),
    status: PropTypes.oneOf(Object.values(CHECKOUT_STATUS)),
    statusMessage: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    totalProductPrice: PropTypes.number.isRequired,
    totalPrice: PropTypes.number.isRequired,
  }).isRequired,
  onButtonClick: PropTypes.func,
};

const defaultProps = {
  buttonTypes: [],
  className: '',
  isPageMyShoppins: true,
  onButtonClick: () => { },
};

const renderCheckoutStateText = (state) => {
  switch (state) {
    case CHECKOUT_STATUS.CONFIRMED:
      return 'Pedido confirmado';
    case CHECKOUT_STATUS.PENDING:
      return 'Pedido a confirmar';
    case CHECKOUT_STATUS.BILLED:
      return 'Pedido facturado';
    case CHECKOUT_STATUS.DISABLED:
      return 'Pedido cancelado';
    case CHECKOUT_STATUS.PENDING_PAYMENT:
      return 'Pendiente de pago';
    case CHECKOUT_STATUS.REJECT_PAYMENT:
      return 'Pago rechazado';
    default:
      return null;
  }
};

const renderCheckoutTypeText = (type) => {
  switch (type) {
    case CHECKOUT_TYPES.ONLINE:
      return 'Compra online';
    case CHECKOUT_TYPES.PHONE:
      return 'Compra telefónica';
    case CHECKOUT_TYPES.ON_SITE:
      return 'Compra en mostrador';
    default:
      return null;
  }
};

class CheckoutCard extends React.Component {
  static buildShippingOptionData(shippingData) {
    if (shippingData.shippingType === OPTION_TYPES.SIMPLE) {
      return {
        shippingOption: {
          optionType: shippingData.shippingType,
          startHourString: shippingData.startHourString,
          endHourString: shippingData.endHourString,
          date: shippingData.date,
          futureDate: shippingData.futureDate,
        },
      };
    } else if (shippingData.shippingType === OPTION_TYPES.SPECIAL) {
      return {
        shippingOption: {
          optionType: shippingData.shippingType,
        },
        timeSlot: {
          startHourString: shippingData.startHourString,
          endHourString: shippingData.endHourString,
          date: shippingData.date,
          futureDate: shippingData.futureDate,
        },
      };
    } else if (shippingData.shippingType === OPTION_TYPES.AGENCY) {
      return {
        shippingOption: {
          name: shippingData.agency.name,
          optionType: shippingData.shippingType,
        },
        timeSlot: {
          startHourString: shippingData.startHourString,
          endHourString: shippingData.endHourString,
          date: shippingData.date,
          futureDate: shippingData.futureDate,
        },
      };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      // NOTE: this only makes sense on mobile devices.
      collapsed: true,
    };

    this._toggleCollapse = this._toggleCollapse.bind(this);
  }

  _toggleCollapse() {
    this.setState(prevState => ({
      collapsed: !prevState.collapsed,
    }));
  }

  render() {
    const checkoutSummaryProps = {
      address: this.props.checkout.address,
      allowCoordinations: this.props.checkout.allowsCoordinations(),
      billingData: this.props.checkout.billingData,
      cardsClickable: true,
      bank: this.props.checkout.bank,
      cart: this.props.checkout.cart,
      checkoutId: this.props.checkout.id,
      isPickup: !this.props.checkout.shippingData,
      installationData: this.props.checkout.installationData,
      paymentMethodId: this.props.checkout.paymentType,
      ...CheckoutCard.buildShippingOptionData(this.props.checkout.shippingData || {}),
    };
    let shippingPrice = 0;
    if (this.props.checkout.shippingData) {
      shippingPrice = this.props.checkout.shippingData.price;
    }
    let agencyExtraCost = 0;
    if (this.props.checkout.shippingData && this.props.checkout.shippingData.agency) {
      agencyExtraCost = this.props.checkout.shippingData.agency.extraCost;
    }

    return (
      <article className={[styles.container, this.props.className].join(' ')}>
        <div className={styles.leftContainer}>
          <div className={styles.cardHeader}>
            <p className={styles.date}>
              {this.props.checkout.date.toFormat('dd/LL/yyyy')}
            </p>
            <p className={styles.purchaseType}>
              {renderCheckoutTypeText(this.props.checkout.checkoutType)}
            </p>
          </div>
          <div>
            <span className={styles.statusText}>
              Estado:
            </span>
            <span className={styles.status}>
              &nbsp;{renderCheckoutStateText(this.props.checkout.status)}
            </span>
          </div>
          <div>
            <span className={styles.statusText}>
              Número de pedido:
            </span>
            <span className={styles.status}>
              &nbsp;{this.props.checkout.id}
            </span>
          </div>
          {
            !this.props.isPageMyShoppins && <CheckoutMessage
              status={this.props.checkout.status}
              paymentMethodId={checkoutSummaryProps.paymentMethodId}
            />
          }
          <div>
            <CheckoutSummary
              {...checkoutSummaryProps}
              onlyProductData
            />
            <CheckoutInvoice
              cart={this.props.checkout.cart}
              extraShippingCost={agencyExtraCost}
              productAmount={calculateCartItemAmount(this.props.checkout.cart)}
              totalProductPrice={this.props.checkout.totalProductPrice}
              totalPrice={this.props.checkout.totalPrice}
              shippingPrice={shippingPrice}
            />
          </div>
          {
            !!this.props.checkout.informationBottom.length &&
            <div className={styles.messageBox}>
              {
                this.props.checkout.informationBottom.map(info => (
                  <p className={styles.informationText} key={info.entries[0].text}>
                    <GlossaryEntry entry={info} />
                  </p>
                ))
              }
            </div>
          }
        </div>
        {
          ScreenStore.screenWidth <= constants.breakpoints.mediumMaxWidth &&
          <button
            className={styles.detailsButton}
            onClick={this._toggleCollapse}
          >
            Ver detalles de pedido
            <div className={styles.chevron}>
              {
                this.state.collapsed ?
                  <img alt="" src={downArrowIcon} /> :
                  <img alt="" src={upArrowIcon} />
              }
            </div>
          </button>
        }
        <div className={styles.rightContainer}>
          {
            (!this.state.collapsed ||
              ScreenStore.screenWidth > constants.breakpoints.mediumMaxWidth) &&
              <React.Fragment>
                <CheckoutSummary
                  {...checkoutSummaryProps}
                  minimalInfo
                />
              </React.Fragment>
          }
          <div className={styles.buttonsContainer}>
            {
              this.props.buttonTypes.includes(BUTTON_TYPES.PRINT) &&
              <SimpleButton
                style={{ margin: 5 }}
                className={styles.printButton}
                onClick={event =>
                  this.props.onButtonClick(event, BUTTON_TYPES.PRINT, this.props.checkout.id)}
              >
                Imprimir Pedido
              </SimpleButton>
            }
            {
              this.props.buttonTypes.includes(BUTTON_TYPES.SHOW_BILLING) &&
              <SimpleButton
                style={{ margin: 5 }}
                className={styles.printButton}
                onClick={() => this.props.onButtonClick(this.props.checkout.tokenBilling)}
              >
                Ver factura
              </SimpleButton>
            }
            {
              this.props.buttonTypes.includes(BUTTON_TYPES.PAY) &&
              <SimpleButton
                style={{ margin: 5 }}
                className={styles.printButton}
                onClick={event =>
                  this.props.onButtonClick(event, BUTTON_TYPES.PAY, this.props.checkout.id)}
              >
                Pagar pedido
              </SimpleButton>
            }
            {
              this.props.buttonTypes.includes(BUTTON_TYPES.CANCEL) &&
              <SimpleButton
                style={{ margin: 5 }}
                className={styles.cancelButton}
                onClick={event =>
                  this.props.onButtonClick(event, BUTTON_TYPES.CANCEL, this.props.checkout.id)}
              >
                Cancelar pedido
              </SimpleButton>
            }
          </div>
        </div>
      </article>
    );
  }
}

CheckoutCard.propTypes = IPropTypes;
CheckoutCard.defaultProps = defaultProps;

const ObservedCheckoutCard = observer(CheckoutCard);

export { ObservedCheckoutCard as CheckoutCard, BUTTON_TYPES };



// WEBPACK FOOTER //
// ./src/common/checkout-card/CheckoutCard.jsx